import FetchClient, { type ApiResponse } from "./FetchClient";

export interface AdminAddProductRequest {
  upc: string;
  name: string;
  storePrice: number;
  onlinePrice: number;
  msrp: number;
  image: string;
  categoryPath: string;
  brand: string;
  inventory: number;
  status: string;
  aisles: string;
  storeId: string;
  sku: string;
}

export interface AdminAddProductResponse {
  message: string;
  upc: string;
  storeId: string;
}

export async function addProductToDatabase(
  productData: AdminAddProductRequest
): Promise<AdminAddProductResponse> {
  const response = await FetchClient.post<ApiResponse<AdminAddProductResponse>>(
    "/admin/products/add",
    productData
  );
  return response.data;
}

// Store Status interfaces and functions
export interface StoreStatusRecord {
  storeId: string;
  lastTimeRun: number;
  duration: number;
  categoriesLeft: number;
  categoriesCompleted: number;
  searchTermsLeft: number;
  searchTermsCompleted: number;
  totalUsers: number;
  lastUpdated: number;
  currentlyRunning: boolean;
  currentItem?: string;
  currentItemType?: string;
  processingStartTime?: number;
}

export interface StoreStatusResponse {
  stores: StoreStatusRecord[];
  total: number;
}

export async function getStoreStatuses(): Promise<StoreStatusResponse> {
  const response = await FetchClient.get<ApiResponse<StoreStatusResponse>>(
    "/admin/store-statuses"
  );
  return response.data;
}

// Product push notification (admin)
export interface AdminProductPushRequest {
  upc: string;
  retailer: string;
  retailerLabel: string;
  storeId: string;
  online: boolean;
  productName: string;
  productUrl: string;
  imageUrl: string;
  price: number;
  msrp: number;
  discountPercent: number;
}

export interface AdminProductPushResponse {
  targeted: number;
  message: string;
}

export async function sendProductPush(
  payload: AdminProductPushRequest
): Promise<AdminProductPushResponse> {
  const response = await FetchClient.post<ApiResponse<AdminProductPushResponse>>(
    "/admin/push/product",
    payload
  );
  return response.data;
}

// Product social cross-post (admin)
export type AdminSocialPlatform = "threads" | "twitter" | "bluesky" | "pinterest";

export interface AdminProductSocialPostRequest {
  productKey: string;
}

export interface AdminProductSocialPostResponse {
  posts: Array<{
    platform: AdminSocialPlatform;
    postId: string;
    scheduledAt: string;
  }>;
  skipped: AdminSocialPlatform[];
  errors: string[];
  affiliateUrl?: string;
}

export async function createProductSocialPost(
  payload: AdminProductSocialPostRequest
): Promise<AdminProductSocialPostResponse> {
  const response = await FetchClient.post<ApiResponse<AdminProductSocialPostResponse>>(
    "/admin/social/product",
    payload
  );
  return response.data;
}
